/* ═══════════════════════════════════════════════════════════════════════════
   MAGHIL ANALYTICS — CUSTOMERS REPORT · DATA LAYER (CUST-* v2.4)
   ---------------------------------------------------------------------------
   Structured mock datasets + pure helpers that drive every P0/P1 story in the
   Customers Report. Most of these metrics map to future ETL phases (Appendix A
   of the spec) with no live endpoint yet, so — exactly like the desktop/mobile
   prototypes (customerd.tsx / customerm.tsx) — the UI is fed from here. Each
   dataset is shaped like the eventual API response so it can be swapped for a
   saga/service call without touching the screen.

   Live data still wins where it exists: the screen merges the live New /
   Returning / segment counts over these defaults (see CustomersReport.tsx).
   ═══════════════════════════════════════════════════════════════════════════ */

import { T } from "./reportTokens";

/* ─── Config thresholds (CUST-0.1 tooltips read these live) ────────────────── */
export const CUST_CONFIG = {
    churnThresholdDays: 60,      // CUST-2.1 — full-service default (configurable per location)
    atRiskMultiplier: 1.5,       // CUST-2.2
    atRiskFloorDays: 14,         // CUST-2.2 — minimum-absence floor
    repeatDisputerThreshold: 2,  // CUST-16.1
    // industryReturnRate removed 2026-09-28: it was an invented benchmark
    // rendered as "Industry avg: 25%" directly beneath the real 30-Day
    // Return Rate. Nothing measured it. If a benchmark is wanted, it has to
    // come from somewhere real, not from this file.
    // CUST-8.1 — directory is no longer capped; page size is a window, not a ceiling.
    directoryPageSizes: [10, 25, 50, 100],
    directoryDefaultPageSize: 25,
    // Above this, an exact COUNT() is too costly and the UI shows "of ~{approx}".
    countEstimateThreshold: 10000,
};

/* ═══════════════════════════════════════════════════════════════════════════
   CUST-0.1 — INFO-ICON TOOLTIP COPY
   Keyed by metric. {threshold}/{multiplier} placeholders are resolved at render
   from CUST_CONFIG so the copy always states the live configured value.
   ═══════════════════════════════════════════════════════════════════════════ */
export const INFO_COPY: Record<string, string> = {
    newCustomers:
        "Customers whose first-ever order at your restaurant falls within the selected date range. A customer can be 'New' only once — every period after that, they're Returning.",
    returning:
        "Customers who ordered in the selected period but whose first-ever visit was before it — the size of your repeat-guest base.",
    returnRate:
        "Of the customers first acquired in a window of the same length that ends 30 days before this period ends, the share who came back and placed another order within 30 days of their first — so each of them has had the full 30 days to return. The card shows the window it measured; \"—\" means nobody was acquired in it. This is not the Acquisition Source card's 30-day repeat, which looks at this period's own new customers.",
    churnRate:
        "Repeat customers who have gone silent past your churn threshold (currently {threshold} days), as a share of the customers who were still active in the comparison period. Higher is worse.",
    atRisk:
        "Repeat customers overdue for a visit — past 1.5× their personal average gap (with a 14-day minimum floor) but not yet churned. Your win-back priority list.",
    avgClv:
        "Average lifetime net sales per customer at this outlet — item sales minus discounts, excluding tax, tips and fees, across all dates. It does not change with the date filter, so there is no comparison.",
    segmentBreakdown:
        "Your customers grouped by RFM behavior (Recency, Frequency, Monetary). Each row shows the customer count, its share of your base, average lifetime spend, and average visits over the trailing 12 months. Segments are evaluated in priority order — a customer matching more than one rule falls into the first match. Hover the ⓘ on any row for that segment's exact rule.",
    cohort:
        "Each row is a group of customers who first visited in the same month — the number next to the month is that month's new customers, the same as the New Customers card for that month. M0 is always 100% (the month they were acquired); reading across shows the share ordering again 1, 2, 3… months later — so you can compare how well different months' acquisitions stick. The grid covers the 12 months ending at the selected date.",
    avgSpendBySegment:
        "Average order value for each RFM segment in the selected period, excluding tax. Shows which segments spend the most per visit.",
    channelPerformance:
        "How your customers split across ordering channels. A guest who orders through more than one channel is counted in each of them, so the rows add up to more than your true guest count — that overlap is the point of the card. The figure beside each bar is that channel's average order value, measured across every order on the channel, including the many that aren't linked to a guest profile.",
    complaints:
        "Customer feedback flagged as negative in the period — a low star rating (≤2), low NPS (≤6), or negative sentiment.",
    cancellations:
        "Orders (or individual items) cancelled in the period. Rate = cancelled ÷ all orders.",
    disputes:
        "Customers with payment disputes. 'Repeat Disputer' flags anyone with 2+ open or lost disputes in the trailing 12 months. Dollar exposure lives in the Payments report, not here.",
    acquisition:
        "Where each customer's first order came from (Google organic/paid, own site, social, marketplace, referral, walk-in/QR), tied to their 30-day repeat rate and average CLV — so budget follows loyalty and value, not raw volume. New and 30-day repeat are for the selected period; Avg CLV and the customer list cover everyone the source has ever brought in. Walk-in / Unattributed = a first order with no digital attribution (no UTM or ad-click data), whatever the order type — in-store, dine-in, pickup or delivery.",
};

/** Resolve {threshold} / {multiplier} placeholders in tooltip copy. */
export function infoCopy(key: string): string {
    const raw = INFO_COPY[key] ?? "";
    return raw
        .replace("{threshold}", String(CUST_CONFIG.churnThresholdDays))
        .replace("{multiplier}", String(CUST_CONFIG.atRiskMultiplier));
}

/* ═══════════════════════════════════════════════════════════════════════════
   CUST-3.2 / 3.3 — RFM SEGMENTS (7 RFM + New = 8 rows)
   Proportional-blocks viz removed (CUST-3.3): this is the sole segment model.
   ═══════════════════════════════════════════════════════════════════════════ */
export interface SegmentDef {
    key: string;
    name: string;
    count: number;
    avgClv: number;      // net lifetime spend, avg (post-discount, post-refund, pre-tax)
    avgVisits: number;   // trailing 12m
    avgSpend: number;    // AOV in period (CUST-12.1)
    color: string;
}

export const SEGMENTS: SegmentDef[] = [
    { key: "champions", name: "Champions", count: 284, avgClv: 1240, avgVisits: 22, avgSpend: 56.2, color: T.accent },
    { key: "loyal", name: "Loyal Regulars", count: 612, avgClv: 680, avgVisits: 14, avgSpend: 48.9, color: T.green },
    { key: "promising", name: "Promising", count: 438, avgClv: 220, avgVisits: 3, avgSpend: 44.1, color: T.blueDark },
    { key: "atrisk", name: "At Risk", count: 356, avgClv: 520, avgVisits: 9, avgSpend: 51.4, color: T.orange },
    { key: "slipping", name: "Slipping Away", count: 520, avgClv: 340, avgVisits: 6, avgSpend: 39.7, color: T.purple },
    { key: "hibernating", name: "Hibernating", count: 680, avgClv: 120, avgVisits: 2, avgSpend: 31.2, color: T.teal },
    { key: "lost", name: "Lost", count: 490, avgClv: 85, avgVisits: 1, avgSpend: 27.8, color: "#94A3B8" },
    { key: "new", name: "New", count: 438, avgClv: 96, avgVisits: 1, avgSpend: 42.5, color: "#0EA5E9" },
];

/* Per-segment definition tooltips (CUST-3.3) — restate the CUST-3.2 assignment
   rules in plain language + exact R/F/M thresholds. Single source of truth for
   the row info icons; must not diverge from the segment-assignment matrix. */
export const SEGMENT_INFO: Record<string, string> = {
    champions:
        "Your best guests — visited very recently, very often, and among your top spenders (Recency, Frequency, and Monetary scores all high: R ≥ 4, F ≥ 4, M ≥ 4). Action: recognize and reward.",
    loyal:
        "Recent, frequent visitors with solid — not top-tier — spend (R ≥ 4, F ≥ 4, M ≥ 2) who don't quite meet the Champion bar. Action: upsell.",
    promising:
        "Newer enthusiasts — visited recently and spend well, but haven't built up visit frequency yet (R ≥ 4, F ≤ 2, M ≥ 3). Action: nurture into regulars.",
    atrisk:
        "Previously frequent, higher-spend guests whose visits have dropped off (R ≤ 3, F ≥ 3, M ≥ 3). Win-back priority.",
    slipping:
        "Mid-tier guests cooling off — typically 30–60 days since their last visit, with moderate frequency and spend (R ≤ 3, F between 2–4, M between 2–4) and not yet At Risk. Action: re-engagement offer.",
    hibernating:
        "Low engagement across the board — a long absence (60–90+ days), infrequent visits, and low spend (R ≤ 2, F ≤ 2, M ≤ 2). Action: low-cost reactivation.",
    lost:
        "No visit in 90+ days and the lowest recency (R = 1). Action: a final reactivation attempt, or write-off.",
    new:
        "Acquired in the last 30 days with 2 or fewer visits — held here until they have enough history (30 days elapsed, or 3+ visits) to be scored into an RFM segment. Action: welcome sequence, first-return incentive.",
};

export const segmentByName = (name?: string | null): SegmentDef | undefined =>
    SEGMENTS.find((s) => s.name === name || s.key === name || s.name.startsWith(String(name ?? "")));

export const segmentColor = (name?: string | null): string => segmentByName(name)?.color ?? T.t3;

/* ═══════════════════════════════════════════════════════════════════════════
   CUST-11.1 — COHORT RETENTION HEATMAP (rolling 12m, M0..M5 shown)
   null = not-yet-elapsed month → renders "—" with neutral background.
   ═══════════════════════════════════════════════════════════════════════════ */
export interface CohortRow {
    cohort: string;
    size: number;
    cells: (number | null)[]; // M0..M5, percentages
}
export const COHORT_DATA: CohortRow[] = [
    { cohort: "Sep '25", size: 420, cells: [100, 32, 24, 19, 16, 14] },
    { cohort: "Oct '25", size: 388, cells: [100, 29, 22, 18, 15, null] },
    { cohort: "Nov '25", size: 465, cells: [100, 34, 26, 20, null, null] },
    { cohort: "Dec '25", size: 402, cells: [100, 28, 21, null, null, null] },
    { cohort: "Jan '26", size: 511, cells: [100, 31, null, null, null, null] },
    { cohort: "Feb '26", size: 438, cells: [100, null, null, null, null, null] },
];

export const cohortColor = (val: number | null): string => {
    if (val === null || val === undefined) return T.hover;
    if (val >= 30) return T.green;
    if (val >= 20) return "rgba(22,163,74,0.55)";
    if (val >= 15) return "rgba(22,163,74,0.35)";
    if (val >= 10) return "rgba(22,163,74,0.2)";
    return "rgba(22,163,74,0.08)";
};

/* ═══════════════════════════════════════════════════════════════════════════
   CUST-13.1 — CHANNEL PERFORMANCE (customers by channel + AOV)
   ═══════════════════════════════════════════════════════════════════════════ */
export interface ChannelRow {
    channel: string;
    customers: number;
    orders: number;
    revenue: number;
    icon: string;
}
export const CHANNEL_PERFORMANCE: ChannelRow[] = [
    { channel: "Dine-In", customers: 1840, orders: 6420, revenue: 309444, icon: "restaurant" },
    { channel: "Pickup", customers: 860, orders: 2180, revenue: 87200, icon: "shopping_bag" },
    { channel: "Delivery", customers: 520, orders: 1240, revenue: 43400, icon: "local_shipping" },
    { channel: "DoorDash", customers: 340, orders: 980, revenue: 31360, icon: "two_wheeler" },
    { channel: "UberEats", customers: 210, orders: 560, revenue: 18480, icon: "electric_moped" },
    { channel: "Grubhub", customers: 108, orders: 290, revenue: 9280, icon: "pedal_bike" },
];

/* ═══════════════════════════════════════════════════════════════════════════
   CUST-14 — COMPLAINTS
   ═══════════════════════════════════════════════════════════════════════════ */
export const COMPLAINTS = {
    total: { current: 87, prior: 75 },
    orders: 4218,
    reasons: [
        { reason: "Long wait time", count: 24 },
        { reason: "Wrong order", count: 18 },
        { reason: "Cold food", count: 15 },
        { reason: "Missing items", count: 12 },
        { reason: "Rude staff", count: 9 },
    ],
};

/* ═══════════════════════════════════════════════════════════════════════════
   CUST-15 — CANCELLED ORDERS / ITEMS
   ═══════════════════════════════════════════════════════════════════════════ */
export const CANCELLATIONS = {
    orders: { current: 124, prior: 132 },
    totalOrders: 4218,
    items: [
        { item: "Truffle Fries", count: 18, reason: "Out of stock", unitPrice: 13 },
        { item: "Wagyu Burger", count: 11, reason: "Kitchen delay", unitPrice: 28 },
        { item: "Lobster Roll", count: 8, reason: "Out of stock", unitPrice: 34 },
        { item: "Caesar Salad", count: 6, reason: "Customer changed mind", unitPrice: 14 },
    ],
};

/* ═══════════════════════════════════════════════════════════════════════════
   CUST-19.1 — ACQUISITION SOURCE ATTRIBUTION (source → volume, repeat, CLV)
   ═══════════════════════════════════════════════════════════════════════════ */
export interface AcquisitionRow {
    key: string;
    source: string;
    icon: string;
    newCount: number;      // in period
    repeatRate: number;    // 30-day, %
    avgClv: number;
    historical: number;    // total historically acquired (for small-sample caution)
}
export const ACQUISITION_SOURCES: AcquisitionRow[] = [
    { key: "direct", source: "Direct / Own Website", icon: "language", newCount: 85, repeatRate: 34, avgClv: 410, historical: 1240 },
    { key: "google_organic", source: "Google — Organic", icon: "search", newCount: 96, repeatRate: 29, avgClv: 320, historical: 1610 },
    { key: "google_paid", source: "Google — Paid Ad", icon: "ads_click", newCount: 120, repeatRate: 18, avgClv: 180, historical: 980 },
    { key: "marketplace", source: "Third-Party Marketplace", icon: "storefront", newCount: 240, repeatRate: 9, avgClv: 95, historical: 3120 },
    { key: "social_organic", source: "Social — Organic", icon: "thumb_up", newCount: 40, repeatRate: 22, avgClv: 210, historical: 520 },
    { key: "social_paid", source: "Meta / Social Paid", icon: "campaign", newCount: 62, repeatRate: 16, avgClv: 165, historical: 8 },
    { key: "email_sms", source: "Email / SMS Campaign", icon: "mail", newCount: 34, repeatRate: 41, avgClv: 380, historical: 410 },
    { key: "qr", source: "QR / Table", icon: "qr_code_2", newCount: 58, repeatRate: 12, avgClv: 130, historical: 690 },
    { key: "referral", source: "Referral", icon: "group_add", newCount: 21, repeatRate: 38, avgClv: 350, historical: 240 },
    { key: "walkin", source: "Walk-in / Unattributed", icon: "storefront", newCount: 310, repeatRate: 15, avgClv: 140, historical: 5400 },
];

export const acquisitionByKey = (key?: string | null) => ACQUISITION_SOURCES.find((a) => a.key === key);

/* ═══════════════════════════════════════════════════════════════════════════
   CUST-5 / CUST-7 / CUST-8 — CUSTOMER DIRECTORY & PROFILES
   A curated set of "hero" customers (named in the spec) with full profile
   detail, then a deterministic generator that pads the directory to 200 so the
   Top-200 row-count dropdown (10/20/50/100/200) and pagination are real.
   ═══════════════════════════════════════════════════════════════════════════ */
export type ChurnStatus = "active" | "at_risk" | "churned";

export interface DisputeRecord {
    date: string;          // chargeback_created_at
    orderId: string;
    amount: number;
    reasonCode: string;
    resolution: "open" | "lost" | "won";
}

export interface OrderItem {
    name: string;
    qty: number;
    price: number;
    refunded?: boolean;
    refundReason?: string;
    cancelled?: boolean;
    cancelReason?: string;
}

export interface DiscountPart {
    source: "Promotional" | "Loyalty" | "Manual & Comp";
    amount: number;
}

export interface CustomerOrder {
    id: string;
    date: string;          // ISO date
    time: string;          // display time
    channel: string;
    items: OrderItem[];
    gross: number;
    discounts: DiscountPart[];
    refund: number;
    cancellation: "none" | "partial" | "full";
    status: "Completed" | "Completed with exceptions" | "Cancelled";
    disputed?: boolean;
    disputeReason?: string;
    refundStaff?: string;
}

export interface RfmDetail {
    r: number; f: number; m: number;
    rAvg: number; fAvg: number; mAvg: number;
    rLabel: string; fLabel: string; mLabel: string;
    computedAt: string;
    prevSegment?: string;
    changeDate?: string;
    changeDirection?: "up" | "down";
}

export interface LoyaltyInfo {
    enrolled: boolean;
    points?: number;
    lifetimeEarned?: number;
    lifetimeRedeemed?: number;
    memberId?: string;
}

export interface CustomerRecord {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    visits: number;
    totalSpend: number;    // net lifetime spend = CLV (item spend − discounts − refunds, ex-tax); reconciles with order-log Net Spend (CUST-7.3)
    lastVisit: string;     // display form
    lastVisitTs: number;   // sort key — the display string is not chronologically sortable
    firstVisit: string;
    segment: string;
    primaryChannel: string;
    churnStatus: ChurnStatus;
    avgDaysBetween: number | null;
    acquisitionKey: string;
    acquisitionMonth: string;
    loyalty: LoyaltyInfo;
    rfm: RfmDetail;
    disputes: DisputeRecord[];
    orders: CustomerOrder[];
}

/**
 * Canonical key for a channel, mirroring the backend's `CHANNEL_KEY_EXPR`:
 * trimmed, lowercased, non-alphanumerics dropped, blanks folded into
 * `unknown`, and the one known `Grbubhub` misspelling collapsed.
 *
 * Needed because `channel_name` is stored inconsistently — `Dinein` (not
 * "Dine-In"), `Doordash`, `GrubHub`, both cases of `Zomato`. Keying icons off
 * the raw value meant almost everything fell through to the generic fallback,
 * including in the live Customer Directory's Primary Channel column. Matching
 * on the key fixes that, and still resolves the mock's own "Dine-In" spelling.
 */
export const channelKey = (ch?: string | null): string => {
    const key = String(ch ?? "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
    if (!key) return "unknown";
    return key === "grbubhub" ? "grubhub" : key;
};

const CHANNEL_ICON: Record<string, string> = {
    instore: "storefront",
    dinein: "restaurant",
    pickup: "shopping_bag",
    delivery: "local_shipping",
    doordash: "two_wheeler",
    ubereats: "electric_moped",
    grubhub: "pedal_bike",
    gloriafood: "restaurant_menu",
    swiggy: "two_wheeler",
    zomato: "two_wheeler",
    seamless: "local_shipping",
    thirdparty: "hub",
    online: "language",
    unknown: "receipt_long",
};
export const channelIcon = (ch?: string | null): string => CHANNEL_ICON[channelKey(ch)] ?? "receipt_long";

/* ─── Curated hero customers (full detail) ────────────────────────────────── */
const HERO_CUSTOMERS: CustomerRecord[] = [
    {
        id: "cust-michael-chen",
        name: "Michael Chen",
        email: "michael.chen@gmail.com",
        phone: "+1 (212) 555-4821",
        visits: 34,
        totalSpend: 4590,
        lastVisit: "Mar 8, 2026",
        lastVisitTs: Date.parse("2026-03-08"),
        firstVisit: "Feb 3, 2024",
        segment: "Champions",
        primaryChannel: "Dine-In",
        churnStatus: "active",
        avgDaysBetween: 6.5,
        acquisitionKey: "direct",
        acquisitionMonth: "Feb 2024",
        loyalty: { enrolled: true, points: 1240, lifetimeEarned: 4820, lifetimeRedeemed: 3580, memberId: "LM-10241" },
        rfm: {
            r: 5, f: 5, m: 5, rAvg: 3.2, fAvg: 3.0, mAvg: 3.1,
            rLabel: "Last visit yesterday", fLabel: "34 visits / 12m", mLabel: "$4,590 lifetime",
            computedAt: "Aug 11, 2026", prevSegment: "Loyal Regular", changeDate: "Feb 15, 2026", changeDirection: "up",
        },
        disputes: [],
        orders: [
            {
                id: "ORD-20260308-4821", date: "2026-03-08", time: "7:42 PM", channel: "Dine-In",
                items: [
                    { name: "Wagyu Burger", qty: 1, price: 28 },
                    { name: "Truffle Fries", qty: 2, price: 13 },
                    { name: "IPA Draft", qty: 1, price: 9 },
                ],
                gross: 78.4, discounts: [{ source: "Promotional", amount: 7.84 }], refund: 0, cancellation: "none", status: "Completed",
            },
            {
                id: "ORD-20260214-3120", date: "2026-02-14", time: "8:05 PM", channel: "Dine-In",
                items: [
                    { name: "Lobster Roll", qty: 1, price: 34, refunded: true, refundReason: "food quality issue" },
                    { name: "Caesar Salad", qty: 1, price: 14, cancelled: true, cancelReason: "cancelled before prep" },
                    { name: "Sparkling Water", qty: 1, price: 6 },
                ],
                gross: 62, discounts: [{ source: "Loyalty", amount: 10 }], refund: 31, cancellation: "partial",
                status: "Completed with exceptions", refundStaff: "A. Rivera",
            },
            {
                id: "ORD-20260129-2210", date: "2026-01-29", time: "1:12 PM", channel: "Pickup",
                items: [{ name: "Wagyu Burger", qty: 2, price: 28 }, { name: "Truffle Fries", qty: 1, price: 13 }],
                gross: 69, discounts: [], refund: 0, cancellation: "none", status: "Completed",
            },
        ],
    },
    {
        id: "cust-lisa-thompson",
        name: "Lisa Thompson",
        email: "lisa.thompson@company.co",
        phone: "+1 (212) 555-7788",
        visits: 14,
        totalSpend: 1735,
        lastVisit: "Feb 28, 2026",
        lastVisitTs: Date.parse("2026-02-28"),
        firstVisit: "May 18, 2024",
        segment: "At Risk",
        primaryChannel: "Dine-In",
        churnStatus: "at_risk",
        avgDaysBetween: 6.5,
        acquisitionKey: "google_paid",
        acquisitionMonth: "May 2024",
        loyalty: { enrolled: true, points: 320, lifetimeEarned: 1820, lifetimeRedeemed: 1500, memberId: "LM-10422" },
        rfm: {
            r: 2, f: 4, m: 4, rAvg: 3.2, fAvg: 3.0, mAvg: 3.1,
            rLabel: "Last visit 10 days ago", fLabel: "14 visits / 12m", mLabel: "$1,735 lifetime",
            computedAt: "Aug 11, 2026", prevSegment: "Loyal Regular", changeDate: "Feb 15, 2026", changeDirection: "down",
        },
        disputes: [],
        orders: [
            {
                id: "ORD-20260228-9910", date: "2026-02-28", time: "6:30 PM", channel: "Dine-In",
                items: [{ name: "Ribeye Steak", qty: 1, price: 42 }, { name: "House Red", qty: 2, price: 12 }],
                gross: 66, discounts: [], refund: 0, cancellation: "none", status: "Completed",
            },
            {
                id: "ORD-20260205-8420", date: "2026-02-05", time: "7:15 PM", channel: "Delivery",
                items: [{ name: "Margherita Pizza", qty: 1, price: 18 }, { name: "Tiramisu", qty: 1, price: 9 }],
                gross: 27, discounts: [{ source: "Manual & Comp", amount: 9 }], refund: 0, cancellation: "none", status: "Completed",
            },
        ],
    },
    {
        id: "cust-david-park",
        name: "David Park",
        email: "d.park@fastmail.com",
        phone: "+1 (212) 555-2044",
        visits: 16,
        totalSpend: 1980,
        lastVisit: "Mar 3, 2026",
        lastVisitTs: Date.parse("2026-03-03"),
        firstVisit: "Jan 9, 2024",
        segment: "Loyal Regulars",
        primaryChannel: "Delivery",
        churnStatus: "active",
        avgDaysBetween: 8,
        acquisitionKey: "marketplace",
        acquisitionMonth: "Jan 2024",
        loyalty: { enrolled: false },
        rfm: {
            r: 5, f: 4, m: 3, rAvg: 3.2, fAvg: 3.0, mAvg: 3.1,
            rLabel: "Last visit 3 days ago", fLabel: "16 visits / 12m", mLabel: "$1,980 lifetime",
            computedAt: "Aug 11, 2026",
        },
        disputes: [
            { date: "2026-03-01", orderId: "ORD-20260118-5501", amount: 42, reasonCode: "product_not_received", resolution: "lost" },
            { date: "2025-11-20", orderId: "ORD-20251110-4410", amount: 36, reasonCode: "fraudulent", resolution: "open" },
        ],
        orders: [
            {
                id: "ORD-20260303-7010", date: "2026-03-03", time: "12:40 PM", channel: "Delivery",
                items: [{ name: "Poke Bowl", qty: 2, price: 16 }], gross: 32, discounts: [], refund: 0, cancellation: "none", status: "Completed",
            },
            {
                id: "ORD-20260118-5501", date: "2026-01-18", time: "6:55 PM", channel: "DoorDash",
                items: [{ name: "3P order — item details unavailable", qty: 1, price: 42 }],
                gross: 42, discounts: [], refund: 0, cancellation: "none", status: "Completed",
                disputed: true, disputeReason: "product_not_received",
            },
        ],
    },
    {
        id: "cust-anna-martinez",
        name: "Anna Martinez",
        email: "anna.m@outlook.com",
        phone: "+1 (212) 555-6631",
        visits: 11,
        totalSpend: 1360,
        lastVisit: "Mar 1, 2026",
        lastVisitTs: Date.parse("2026-03-01"),
        firstVisit: "Jul 22, 2025",
        segment: "Promising",
        primaryChannel: "DoorDash",
        churnStatus: "active",
        avgDaysBetween: 12,
        acquisitionKey: "social_organic",
        acquisitionMonth: "Jul 2025",
        loyalty: { enrolled: true, points: 210, lifetimeEarned: 1430, lifetimeRedeemed: 1220, memberId: "LM-11876" },
        rfm: {
            r: 5, f: 1, m: 4, rAvg: 3.2, fAvg: 3.0, mAvg: 3.1,
            rLabel: "Last visit 2 days ago", fLabel: "11 visits / 12m", mLabel: "$1,360 lifetime",
            computedAt: "Aug 11, 2026", prevSegment: "New", changeDate: "Jan 5, 2026", changeDirection: "up",
        },
        disputes: [],
        orders: [
            {
                id: "ORD-20260301-3300", date: "2026-03-01", time: "1:20 PM", channel: "DoorDash",
                items: [{ name: "3P order — item details unavailable", qty: 1, price: 44 }],
                gross: 44, discounts: [{ source: "Promotional", amount: 8 }], refund: 0, cancellation: "none", status: "Completed",
            },
        ],
    },
];

/* ─── Deterministic generator (no Math.random — stable across renders) ─────── */
const FIRST_NAMES = ["Sarah", "James", "Emily", "Robert", "Olivia", "Daniel", "Sophia", "William", "Ava", "Ethan", "Mia", "Noah", "Isabella", "Liam", "Grace", "Lucas", "Chloe", "Mason", "Zoe", "Henry", "Nora", "Owen", "Ruby", "Leo"];
const LAST_NAMES = ["Williams", "Rodriguez", "Davis", "Kim", "Nguyen", "Patel", "Garcia", "Brown", "Wilson", "Lee", "Walker", "Hall", "Young", "Turner", "Cooper", "Reed", "Bailey", "Murphy", "Rivera", "Gomez", "Foster", "Bryant", "Hughes", "Ross"];
const CHANNELS = ["Dine-In", "Pickup", "Delivery", "DoorDash", "UberEats", "Grubhub"];
const MENU = [
    { name: "Wagyu Burger", price: 28 }, { name: "Truffle Fries", price: 13 }, { name: "Margherita Pizza", price: 18 },
    { name: "Caesar Salad", price: 14 }, { name: "Ribeye Steak", price: 42 }, { name: "Poke Bowl", price: 16 },
    { name: "Lobster Roll", price: 34 }, { name: "IPA Draft", price: 9 }, { name: "Tiramisu", price: 9 },
];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Small seeded LCG → deterministic pseudo-random in [0,1). */
function rng(seed: number): () => number {
    let s = (seed * 9301 + 49297) % 233280;
    return () => {
        s = (s * 9301 + 49297) % 233280;
        return s / 233280;
    };
}

function genOrders(seed: number, count: number, channel: string): CustomerOrder[] {
    const r = rng(seed + 7);
    const n = Math.min(count, 6);
    const out: CustomerOrder[] = [];
    for (let i = 0; i < n; i++) {
        const itemA = MENU[Math.floor(r() * MENU.length)];
        const itemB = MENU[Math.floor(r() * MENU.length)];
        const gross = Math.round((itemA.price + itemB.price) * (1 + r()));
        const hasDisc = r() > 0.6;
        const month = 3 - Math.floor(i / 2);
        const day = 1 + Math.floor(r() * 27);
        out.push({
            id: `ORD-2026${String(month).padStart(2, "0")}${String(day).padStart(2, "0")}-${1000 + Math.floor(r() * 8999)}`,
            date: `2026-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
            time: `${1 + Math.floor(r() * 11)}:${String(Math.floor(r() * 59)).padStart(2, "0")} PM`,
            channel,
            items: [
                { name: itemA.name, qty: 1, price: itemA.price },
                { name: itemB.name, qty: 1 + Math.floor(r() * 2), price: itemB.price },
            ],
            gross,
            discounts: hasDisc ? [{ source: "Promotional", amount: Math.round(gross * 0.1) }] : [],
            refund: 0,
            cancellation: "none",
            status: "Completed",
        });
    }
    return out;
}

/**
 * Builds the whole customer base. Two deliberate properties:
 *
 * 1. Segment membership is dealt out to match SEGMENTS counts exactly, so the
 *    directory total (3,818) reconciles with the Segment Breakdown subtitle and
 *    every segment drill title ("Champions — 284 Customers" really is 284 rows).
 * 2. Order history is NOT generated here. A real list endpoint returns profile
 *    rows only; orders are fetched per customer when a profile opens
 *    (getCustomerById), which also keeps building 3.8k records cheap.
 */
function generateCustomers(): CustomerRecord[] {
    const out: CustomerRecord[] = [];
    // Heroes occupy a slot in their own segment so the totals stay exact.
    const heroesBySegment = new Map<string, number>();
    HERO_CUSTOMERS.forEach((h) => heroesBySegment.set(h.segment, (heroesBySegment.get(h.segment) ?? 0) + 1));

    let i = 0;
    for (const seg of SEGMENTS) {
        const quota = seg.count - (heroesBySegment.get(seg.name) ?? 0);
        for (let k = 0; k < quota; k++, i++) {
        const r = rng(i + 101);
        const first = FIRST_NAMES[Math.floor(r() * FIRST_NAMES.length)];
        const last = LAST_NAMES[Math.floor(r() * LAST_NAMES.length)];
        const name = `${first} ${last}`;
        // Spend/visits centre on the segment's own averages, so the per-segment
        // Avg CLV and Avg Visits columns stay honest against the member rows.
        const totalSpend = Math.max(1, Math.round(seg.avgClv * (0.55 + r() * 0.95)));
        const visits = Math.max(1, Math.round(seg.avgVisits * (0.6 + r() * 0.9)));
        const channel = CHANNELS[Math.floor(r() * CHANNELS.length)];
        const hasEmail = r() > 0.12;
        const hasPhone = r() > 0.08;
        const acq = ACQUISITION_SOURCES[Math.floor(r() * ACQUISITION_SOURCES.length)];
        const churnRoll = r();
        const churnStatus: ChurnStatus = churnRoll > 0.85 ? "churned" : churnRoll > 0.7 ? "at_risk" : "active";
        // Spread last visits across ~5 months so the Last Visit sort is meaningful.
        const daysAgo = Math.floor(r() * 150);
        const lastVisitDate = new Date(Date.UTC(2026, 2, 31) - daysAgo * 86400000);
        const lastVisitDay = lastVisitDate.getUTCDate();
        const acqMonth = Math.floor(r() * 12);
        const acqYear = 2024 + Math.floor(r() * 2);
        const enrolled = r() > 0.45;
        const rVal = 1 + Math.floor(r() * 5);
        const fVal = 1 + Math.floor(r() * 5);
        const mVal = 1 + Math.floor(r() * 5);
        out.push({
            id: `cust-gen-${i}`,
            name,
            email: hasEmail ? `${first.toLowerCase()}.${last.toLowerCase()}@${["gmail.com", "outlook.com", "fakeemail.com", "company.co"][Math.floor(r() * 4)]}` : null,
            phone: hasPhone ? `+1 (212) 555-${String(1000 + Math.floor(r() * 8999)).slice(0, 4)}` : null,
            visits,
            totalSpend,
            lastVisit: `${MONTHS[lastVisitDate.getUTCMonth()]} ${lastVisitDay}, ${lastVisitDate.getUTCFullYear()}`,
            lastVisitTs: lastVisitDate.getTime(),
            firstVisit: `${MONTHS[acqMonth]} ${1 + Math.floor(r() * 27)}, ${acqYear}`,
            segment: seg.name,
            primaryChannel: channel,
            churnStatus,
            avgDaysBetween: visits >= 3 ? Math.round((4 + r() * 20) * 10) / 10 : null,
            acquisitionKey: acq.key,
            acquisitionMonth: `${MONTHS[acqMonth]} ${acqYear}`,
            loyalty: enrolled
                ? { enrolled: true, points: Math.round(r() * 900), lifetimeEarned: Math.round(totalSpend), lifetimeRedeemed: Math.round(totalSpend * 0.7), memberId: `LM-${20000 + i}` }
                : { enrolled: false },
            rfm: {
                r: rVal, f: fVal, m: mVal, rAvg: 3.2, fAvg: 3.0, mAvg: 3.1,
                rLabel: `Last visit ${lastVisitDay} days ago`, fLabel: `${visits} visits / 12m`, mLabel: `$${totalSpend.toLocaleString()} lifetime`,
                computedAt: "Aug 11, 2026",
            },
            disputes: churnRoll > 0.92 ? [{ date: "2026-02-10", orderId: `ORD-gen-${i}`, amount: Math.round(r() * 60), reasonCode: "product_not_received", resolution: "open" }] : [],
            orders: [],   // hydrated on demand — see getCustomerById
        });
        }
    }
    return out;
}

/* Full directory — every customer, not a top-N slice (CUST-8.1). */
let _directory: CustomerRecord[] | null = null;
export function getCustomerDirectory(): CustomerRecord[] {
    if (_directory) return _directory;
    _directory = [...HERO_CUSTOMERS, ...generateCustomers()].sort((a, b) => b.totalSpend - a.totalSpend);
    return _directory;
}

/**
 * Profile lookup. Order history is hydrated here rather than at generation time,
 * mirroring the real split between the list endpoint (profile rows only) and the
 * per-customer profile endpoint (full order detail).
 */
export function getCustomerById(id?: string | null): CustomerRecord | undefined {
    if (!id) return undefined;
    const c = getCustomerDirectory().find((x) => x.id === id);
    if (c && c.orders.length === 0) {
        const seed = Number(String(c.id).replace(/\D/g, "")) || 1;
        c.orders = genOrders(seed, c.visits, c.primaryChannel);
    }
    return c;
}

/* ─── Drill-set selectors (CUST-6 / CUST-2.2 / CUST-19.2) ──────────────────── */
export function customersForSegment(segmentName: string): CustomerRecord[] {
    return getCustomerDirectory().filter((c) => c.segment === segmentName);
}
export function customersNew(): CustomerRecord[] {
    return getCustomerDirectory().filter((c) => c.segment === "New" || c.visits <= 2);
}
export function customersReturning(): CustomerRecord[] {
    return getCustomerDirectory().filter((c) => c.visits > 2).sort((a, b) => b.totalSpend - a.totalSpend);
}
export function customersAtRisk(): CustomerRecord[] {
    return getCustomerDirectory().filter((c) => c.churnStatus === "at_risk").sort((a, b) => b.totalSpend - a.totalSpend);
}
export function customersForAcquisition(key: string): CustomerRecord[] {
    return getCustomerDirectory().filter((c) => c.acquisitionKey === key);
}

/* ═══════════════════════════════════════════════════════════════════════════
   CUST-8.1 — CUSTOMER DIRECTORY (ALL CUSTOMERS, SERVER-SIDE PAGINATION)
   ---------------------------------------------------------------------------
   Stands in for the list endpoint. Search, sort and paging all happen HERE —
   over the full base, never client-side over a loaded slice — so the component
   receives exactly one page and the swap to a real API is a change of transport
   only. Rows carry pre-masked contact fields: full email/phone are never handed
   to the directory view, which is what makes the "no eye toggles here" rule
   enforceable rather than merely a UI convention.
   ═══════════════════════════════════════════════════════════════════════════ */
export type DirectorySortKey = "name" | "visits" | "totalSpend" | "lastVisit" | "segment" | "primaryChannel";

export interface DirectoryQuery {
    page: number;             // 1-based
    pageSize: number;
    sortKey: DirectorySortKey;
    sortDir: "asc" | "desc";
    search?: string;
    /**
     * Opt-in contact reveal (the column eye toggles). Modelled as an explicit
     * request parameter rather than shipping full contacts with every page:
     * the unmasked values are only produced when they are actually asked for,
     * which is the point at which a real endpoint applies the permission check
     * and writes the access to the audit log.
     */
    reveal?: { email?: boolean; phone?: boolean };
}

/** One directory row. `email`/`phone` hold the DISPLAY value the server chose
 *  to return — masked unless the query explicitly requested a reveal. */
export interface DirectoryRow {
    id: string;
    rank: number;             // continuous across pages
    name: string;
    visits: number;
    totalSpend: number;
    lastVisit: string;
    segment: string;
    primaryChannel: string;
    email: string;
    phone: string;
    repeatDisputer: boolean;
}

export interface DirectoryPage {
    rows: DirectoryRow[];
    total: number;
    approximate: boolean;     // true when total is an estimate, rendered as "~N"
    start: number;            // 1-based inclusive
    end: number;              // 1-based inclusive
    page: number;
    totalPages: number;
}

const sortValue = (c: CustomerRecord, key: DirectorySortKey): string | number => {
    switch (key) {
        case "visits": return c.visits;
        case "totalSpend": return c.totalSpend;
        case "lastVisit": return c.lastVisitTs;
        case "segment": return c.segment;
        case "primaryChannel": return c.primaryChannel;
        default: return c.name;
    }
};

/** Search matches name, or last-4 phone digits (per the story). */
function matchesSearch(c: CustomerRecord, q: string): boolean {
    if (c.name.toLowerCase().includes(q)) return true;
    const digits = q.replace(/\D/g, "");
    if (digits.length >= 2 && c.phone) return c.phone.replace(/\D/g, "").slice(-4).includes(digits);
    return false;
}

/** Resolve a query to a single page. Async to mirror the real endpoint. */
export function fetchDirectoryPage(q: DirectoryQuery): Promise<DirectoryPage> {
    const all = getCustomerDirectory();
    const search = (q.search ?? "").trim().toLowerCase();

    const filtered = search ? all.filter((c) => matchesSearch(c, search)) : all;

    // Sort across the WHOLE filtered set, with customer id as the tiebreaker —
    // the same (sort_key, customer_id) ordering the keyset pagination relies on,
    // so page boundaries are stable and rows can't be skipped or repeated.
    const dir = q.sortDir === "asc" ? 1 : -1;
    const sorted = [...filtered].sort((a, b) => {
        const av = sortValue(a, q.sortKey), bv = sortValue(b, q.sortKey);
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return a.id.localeCompare(b.id);
    });

    const total = sorted.length;
    // pageSize <= 0 is the "All" sentinel: one page holding the entire result set.
    const size = q.pageSize > 0 ? q.pageSize : Math.max(1, total);
    const totalPages = Math.max(1, Math.ceil(total / size));
    const page = Math.min(Math.max(1, q.page), totalPages);
    const offset = (page - 1) * size;
    const slice = sorted.slice(offset, offset + size);

    const rows: DirectoryRow[] = slice.map((c, i) => ({
        id: c.id,
        rank: offset + i + 1,
        name: c.name,
        visits: c.visits,
        totalSpend: c.totalSpend,
        lastVisit: c.lastVisit,
        segment: c.segment,
        primaryChannel: c.primaryChannel,
        email: q.reveal?.email ? (c.email ?? "—") : maskEmail(c.email),
        phone: q.reveal?.phone ? (c.phone ?? "—") : phoneLast4(c.phone),
        repeatDisputer: isRepeatDisputer(c),
    }));

    return Promise.resolve({
        rows,
        total,
        approximate: total > CUST_CONFIG.countEstimateThreshold,
        start: total === 0 ? 0 : offset + 1,
        end: offset + rows.length,
        page,
        totalPages,
    });
}

/**
 * Export feed (CUST-9.2) — the FULL filtered/sorted set, independent of the
 * current page. Contacts are unmasked only when consent was given.
 */
export function fetchDirectoryExport(q: Omit<DirectoryQuery, "page" | "pageSize">, includeContacts: boolean) {
    const all = getCustomerDirectory();
    const search = (q.search ?? "").trim().toLowerCase();
    const filtered = search ? all.filter((c) => matchesSearch(c, search)) : all;
    const dir = q.sortDir === "asc" ? 1 : -1;
    const sorted = [...filtered].sort((a, b) => {
        const av = sortValue(a, q.sortKey), bv = sortValue(b, q.sortKey);
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return a.id.localeCompare(b.id);
    });
    return sorted.map((c, i) => ({
        rank: i + 1,
        customer: c,
        email: includeContacts ? (c.email ?? "—") : maskEmail(c.email),
        phone: includeContacts ? (c.phone ?? "—") : phoneLast4(c.phone),
    }));
}

/* ═══════════════════════════════════════════════════════════════════════════
   MASKING (CUST-5.1 / CUST-8.1)
   ═══════════════════════════════════════════════════════════════════════════ */
/** mic•••••@gm•••.com — first 3 of local, first 2 of domain label, full TLD. */
export function maskEmail(email?: string | null): string {
    if (!email) return "—";
    const at = email.indexOf("@");
    if (at < 0) return email;
    const local = email.slice(0, at);
    const domain = email.slice(at + 1);
    const dot = domain.lastIndexOf(".");
    if (local.length <= 3) return email; // too short to mask meaningfully
    const tld = dot >= 0 ? domain.slice(dot) : "";
    const domLabel = dot >= 0 ? domain.slice(0, dot) : domain;
    const maskedLocal = local.slice(0, 3) + "•••••";
    const maskedDom = domLabel.slice(0, 2) + "•••";
    return `${maskedLocal}@${maskedDom}${tld}`;
}

/** •••• •••• 4821 — only last 4 digits visible. */
export function maskPhone(phone?: string | null): string {
    if (!phone) return "—";
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 4) return phone;
    return `•••• •••• ${digits.slice(-4)}`;
}

export const phoneLast4 = (phone?: string | null): string => {
    if (!phone) return "—";
    const digits = phone.replace(/\D/g, "");
    return digits.length >= 4 ? `•••• •••• ${digits.slice(-4)}` : phone;
};

/* ═══════════════════════════════════════════════════════════════════════════
   ORDER-LOG DERIVED TOTALS (CUST-7.3 footer)
   ═══════════════════════════════════════════════════════════════════════════ */
export function orderDiscountTotal(o: CustomerOrder): number {
    return o.discounts.reduce((s, d) => s + d.amount, 0);
}
export function orderLogTotals(orders: CustomerOrder[]) {
    // gross = Item Total (pre-discount menu value, ex-tax); paid = gross − discounts;
    // net (= CLV basis) = paid − refunds. The three reconcile on every row and here.
    const gross = orders.reduce((s, o) => s + o.gross, 0);
    const discounts = orders.reduce((s, o) => s + orderDiscountTotal(o), 0);
    const refunds = orders.reduce((s, o) => s + o.refund, 0);
    const paid = gross - discounts;
    const cancelled = orders.filter((o) => o.cancellation === "full").length;
    return { count: orders.length, gross, discounts, refunds, paid, net: paid - refunds, cancelled };
}

/** Trailing-12m open/lost dispute count → repeat-disputer flag (CUST-16.1). */
export function openLostDisputes(c: CustomerRecord): number {
    return c.disputes.filter((d) => d.resolution === "open" || d.resolution === "lost").length;
}
export function isRepeatDisputer(c: CustomerRecord): boolean {
    return openLostDisputes(c) >= CUST_CONFIG.repeatDisputerThreshold;
}
